/**
 * Client-side P10/P50/P90 type curve for an arbitrary well selection (map lasso).
 * Mirrors asset-core/d2d_asset/typecurves.py: time-zero = peak within first 12
 * producing months; P10 = high (exceedance), linear interpolation.
 */
export function percentile(values, p) {
  const xs = [...values].sort((a, b) => a - b);
  if (!xs.length) return NaN;
  const pos = (1 - p / 100) * (xs.length - 1);
  const lo = Math.floor(pos);
  const hi = Math.min(lo + 1, xs.length - 1);
  return xs[lo] + (xs[hi] - xs[lo]) * (pos - lo);
}

export function typeCurve(wells, { months = 120, minWells = 3 } = {}) {
  const aligned = [];
  const used = [];
  for (const w of wells) {
    let prod = (w.history || []).map((r) => r[1]).filter((q) => q > 0);
    if (prod.length < 6) continue;
    const head = prod.slice(0, 12);
    const pk = head.indexOf(Math.max(...head));
    prod = prod.slice(pk);
    aligned.push(prod.slice(0, months));
    used.push(w.assetId);
  }
  const curves = { P10: [], P50: [], P90: [], mean: [], count: [] };
  // Same survivor-bias floor as asset-core typecurves.py: stop at < 40 % of wells.
  const floor = Math.max(minWells, Math.ceil(0.4 * aligned.length));
  for (let k = 0; k < months; k += 1) {
    const vals = aligned.filter((s) => s.length > k).map((s) => s[k]);
    if (vals.length < floor) break;
    curves.P10.push(percentile(vals, 10));
    curves.P50.push(percentile(vals, 50));
    curves.P90.push(percentile(vals, 90));
    curves.mean.push(vals.reduce((a, b) => a + b, 0) / vals.length);
    curves.count.push(vals.length);
  }
  const eurs = wells.map((w) => w.fit?.eur).filter((v) => v > 0);
  const eurDist =
    eurs.length >= minWells
      ? { P10: percentile(eurs, 10), P50: percentile(eurs, 50), P90: percentile(eurs, 90) }
      : null;
  return { wellCount: used.length, wellIds: used, months: curves.P50.length, curves, eurWellDistribution: eurDist };
}
